/**
 * `/slides` — library of saved presentations. "New" creates a blank persisted
 * deck; "Open" imports a file into a new one. Cards open `/slides/$pageId`.
 */
import { createMemo, createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Presentation as PresentationIcon } from "@fleet/icons";
import { createDocumentPageMutation, DECK_BLOCK_TYPE } from "@folio/shared";
import { useDeckPagesQuery } from "@folio/shared/solid";
import { useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { importOdp, importPptx, type Presentation } from "../lib/office-io";
import { emptyPresentation } from "../lib/presentation-ops";
import { stripExtension } from "../lib/download";
import { presentationToBlockNode } from "../lib/document-blocks";
import { DocumentLibrary } from "./document-library";

const IMPORTERS: Record<string, (file: Blob) => Promise<Presentation>> = {
  pptx: importPptx,
  odp: importOdp,
};

export function SlidesLibrary() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = createSignal<false | "new" | "import">(false);

  const decks = useDeckPagesQuery({
    db: () => db(),
    drizzle,
    userId: () => userId(),
  });
  const items = createMemo(() => decks.data ?? []);

  const ready = () => Boolean(drizzle() && userId());

  const create = async (deck: Presentation, title: string) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid) {
      return;
    }
    const node = presentationToBlockNode("", deck);
    const id = await createDocumentPageMutation(dbInstance, uid, DECK_BLOCK_TYPE, {
      title,
      content: node.content,
      text: node.text,
    });
    void navigate({ to: "/slides/$pageId", params: { pageId: id } });
  };

  const handleNew = async () => {
    if (busy()) {
      return;
    }
    setBusy("new");
    try {
      await create(emptyPresentation(), "");
    } catch (err) {
      console.error("[slides] create failed:", err);
      showToast(t(msg`Could not create the presentation`), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenFile = async (file: File) => {
    if (busy()) {
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const importer = IMPORTERS[ext];
    if (!importer) {
      showToast(t(msg`Unsupported presentation file`), "error");
      return;
    }
    setBusy("import");
    try {
      const deck = await importer(file);
      await create(deck.slides.length > 0 ? deck : emptyPresentation(), stripExtension(file.name));
    } catch (err) {
      console.error("[slides] import failed:", err);
      showToast(t(msg`Could not import that file`), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DocumentLibrary
      testid="slides"
      icon={PresentationIcon}
      heading={t(msg`Presentations`)}
      items={items}
      isLoading={() => decks.isLoading}
      ready={ready}
      busy={busy}
      newLabel={t(msg`New presentation`)}
      emptyTitle={t(msg`No presentations yet`)}
      emptyDescription={t(msg`Create a presentation or open a file to get started.`)}
      untitledLabel={t(msg`Untitled presentation`)}
      accept=".pptx,.odp"
      onNew={() => void handleNew()}
      onOpenFile={(file) => void handleOpenFile(file)}
      onOpen={(pageId) => void navigate({ to: "/slides/$pageId", params: { pageId } })}
    />
  );
}
